import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Star, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminListProducts, deleteProduct, updateProduct, reorderProducts } from "@/lib/adminApi";
import { fileUrl } from "@/lib/adminApi";
import ImageWithFallback from "@/components/ImageWithFallback";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    adminListProducts().then(setProducts).catch(() => toast.error("Failed to load products")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toPayload = (p) => ({
    name: p.name, category: p.category, slug: p.slug, code: p.code, category_name: p.category_name,
    tagline: p.tagline, short_description: p.short_description, description: p.description,
    composition: p.composition, benefits: p.benefits, technical_highlights: p.technical_highlights,
    dosage: p.dosage, application: p.application, storage: p.storage, presentation: p.presentation,
    image: p.image, gallery: p.gallery, brochure_url: p.brochure_url, seo_title: p.seo_title,
    seo_description: p.seo_description, status: p.status, featured: p.featured, order: p.order, related: p.related,
  });

  const toggle = async (p, field) => {
    setBusy(p.slug);
    try {
      const updated = { ...p, [field]: field === "status" ? (p.status === "published" ? "draft" : "published") : !p[field] };
      await updateProduct(p.slug, toPayload(updated));
      setProducts((prev) => prev.map((x) => (x.slug === p.slug ? updated : x)));
      toast.success("Updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy("");
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= products.length) return;
    const next = [...products];
    [next[index], next[target]] = [next[target], next[index]];
    const withOrder = next.map((p, i) => ({ ...p, order: i + 1 }));
    setProducts(withOrder);
    try {
      await reorderProducts(withOrder.map((p) => ({ slug: p.slug, order: p.order })));
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  const remove = async (slug) => {
    try {
      await deleteProduct(slug);
      setProducts((prev) => prev.filter((p) => p.slug !== slug));
      toast.success("Product deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold" style={{ color: "#063B63" }}>Products</h1>
          <p className="text-sm mt-1" style={{ color: "#4A6B82" }}>Add, edit, publish, reorder and manage product images & brochures.</p>
        </div>
        <Link to="/admin/products/new" data-testid="admin-add-product" className="btn-primary !py-2.5"><Plus className="w-4 h-4" /> Add Product</Link>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#087FBF" }} /></div>
      ) : (
        <div className="biosyn-card !p-0 overflow-hidden">
          <div className="divide-y" style={{ borderColor: "rgba(8,127,191,0.1)" }}>
            {products.map((p, i) => (
              <div key={p.slug} data-testid={`admin-product-${p.slug}`} className="flex items-center gap-4 p-4">
                <div className="flex flex-col gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded disabled:opacity-30" style={{ color: "#087FBF" }} data-testid={`move-up-${p.slug}`}><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === products.length - 1} className="p-1 rounded disabled:opacity-30" style={{ color: "#087FBF" }} data-testid={`move-down-${p.slug}`}><ArrowDown className="w-4 h-4" /></button>
                </div>
                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 flex items-center justify-center" style={{ background: "#F5FAFC" }}>
                  <ImageWithFallback src={fileUrl(p.image)} alt={p.name} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-heading font-bold truncate" style={{ color: "#063B63" }}>{p.name}</p>
                    {p.featured && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white" style={{ background: "#3FAE35" }}>FEATURED</span>}
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={p.status === "published" ? { background: "rgba(63,174,53,0.15)", color: "#197A35" } : { background: "rgba(200,138,0,0.15)", color: "#8a6400" }}>{p.status.toUpperCase()}</span>
                  </div>
                  <p className="text-xs truncate" style={{ color: "#4A6B82" }}>{p.category_name} • {p.presentation}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggle(p, "featured")} disabled={busy === p.slug} title="Toggle featured" className="p-2 rounded-lg border" style={{ borderColor: "rgba(8,127,191,0.15)", color: p.featured ? "#3FAE35" : "#94a3b8" }} data-testid={`toggle-featured-${p.slug}`}><Star className="w-4 h-4" /></button>
                  <button onClick={() => toggle(p, "status")} disabled={busy === p.slug} title="Publish/unpublish" className="p-2 rounded-lg border" style={{ borderColor: "rgba(8,127,191,0.15)", color: p.status === "published" ? "#087FBF" : "#94a3b8" }} data-testid={`toggle-status-${p.slug}`}>{p.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                  <Link to={`/admin/products/${p.slug}`} className="p-2 rounded-lg border" style={{ borderColor: "rgba(8,127,191,0.15)", color: "#087FBF" }} data-testid={`edit-${p.slug}`}><Pencil className="w-4 h-4" /></Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 rounded-lg border" style={{ borderColor: "rgba(192,57,43,0.2)", color: "#c0392b" }} data-testid={`delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                        <AlertDialogDescription>This will permanently remove the product from the catalogue.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(p.slug)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
